import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';
import type { SupportedLocale } from '../../utils/routing/locale-from-pathname';

/** CV content (title/experience/education/projects/skills) is kept independently per language. */
export type ProfileLocale = SupportedLocale;
export const PROFILE_LOCALES: ProfileLocale[] = ['en', 'fi', 'sv'];

export interface Company {
  name: string;
  website: string;
  logoUrl: string;
  about: string;
  address: string;
  companyValues: string[];
  numberOfEmployees: number;
  industry: string;
  contactDetails: string;
}

export interface JobDetails {
  title: string;
  normalisedTitle: string;
  normalisedRoleLevel: string;
  description: string;
  locations: string[];
  remoteness: string;
  jobType: string;
  salary: number;
  /** Current work percentage (1–100). Used to compute 100% FTE equivalent for match score. */
  workPercentage: number;
  vacationWeeks: number;
  benefits: string[];
  /** Languages the user speaks at professional level (for match score). */
  languages: string[];
  company: Company;
}

export interface ProfileWorkExperience {
  id: string;
  startDate: string;
  endDate: string;
  title: string;
  company: string;
  description?: string;
}

export interface ProfileEducation {
  id: string;
  startDate: string;
  endDate: string;
  degree: string;
  institution: string;
}

export interface ProfileProject {
  id: string;
  year: string;
  title: string;
  description: string;
}

/** CV content for a single language. Independently edited/filled per {@link ProfileLocale}. */
export interface ProfileCvContent {
  profileTitle: string;
  profileWorkExperiences: ProfileWorkExperience[];
  profileEducation: ProfileEducation[];
  profileProjects: ProfileProject[];
  profileSkills: string[];
}

export type ProfileCvByLocale = Record<ProfileLocale, ProfileCvContent>;

/**
 * A saved starting point for the map's commute search (SRP map view), so it can default there
 * instead of always requiring a map click. Geocoded client-side when the user enters it in their
 * profile (see Profile.tsx), only the resulting label + coordinates are stored here, same as any
 * other profile field, and synced to UserState like the rest of this slice.
 */
export interface HomeAddress {
  label: string;
  point: { lat: number; lng: number };
}

/** How the user usually gets to work; picks which travel time leads on the job page. Null = public transport. */
export type CommuteMode = 'transit' | 'driving' | 'cycling' | 'walking';
export const COMMUTE_MODES: CommuteMode[] = ['transit', 'driving', 'cycling', 'walking'];

/**
 * Work-authorization status for Finland/EU, used to answer the near-universal screening question
 * every ATS application form asks (confirmed live on Teamtailor's own `answers_attributes` custom
 * questions during TEC-489 discovery). Deliberately a fixed status, not free text: at fill time the
 * autofill recipe/LLM matches this status against whatever exact radio-button wording a given
 * company's form uses, rather than us trying to predict every company's phrasing here.
 */
export type WorkAuthorizationStatus =
  | ''
  | 'eu_eea_citizen'
  | 'finnish_citizen'
  | 'permanent_resident'
  | 'valid_work_permit'
  | 'requires_sponsorship'
  | 'not_authorized';

/**
 * Contact + application-specific details a CV parse doesn't naturally produce (confirmed by
 * reading profile-cv.ts's actual extraction schema during TEC-489 planning: no name/email/phone/
 * work-authorization/salary fields exist anywhere in the profile today). Powers Application
 * Autofill (TEC-489) form-fill in addition to being generally useful contact info. `email`
 * defaults from the Auth0 session on first use (see Profile.tsx) but is independently stored/
 * editable, a user may want a different application-facing address.
 */
export interface ApplicationDetails {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  workAuthorization: WorkAuthorizationStatus;
  /** Annual salary expectation in the user's local currency, 0 = unset. */
  salaryExpectation: number;
}

export interface JobFormState {
  /** User-uploaded profile picture (JPEG data URL). Overrides OAuth avatar when set. */
  profilePhotoDataUrl: string;
  /** S3 object key of the uploaded CV PDF (cv-uploads/{userId}/{uuid}.pdf), not a URL. */
  cvFileKey: string;
  linkedInUrl: string;
  /** CV content (title/experience/education/projects/skills), independent per language, not auto-translated. */
  profileCvByLocale: ProfileCvByLocale;
  /** Optional saved commute-search starting point; null until the user sets one. */
  homeAddress: HomeAddress | null;
  /** Preferred way of commuting, shown first on the job page's travel-time strip; null means public transport. */
  preferredCommuteMode: CommuteMode | null;
  /** Contact + application details (TEC-489 Application Autofill), independent of CV content. */
  applicationDetails: ApplicationDetails;
  currentJob: JobDetails;
  desiredJob: {
    titles: string[];
    normalisedRoleLevels: string[];
    locations: string[];
    remoteness: string[];
    jobTypes: string[];
    salaryMin: number;
    vacationWeeks: number;
    benefits: string[];
    companyIndustries: string[];
  };
}

const initialJobDetails: JobDetails = {
  title: '',
  normalisedTitle: '',
  normalisedRoleLevel: '',
  description: '',
  locations: [],
  remoteness: '',
  jobType: '',
  salary: 0,
  workPercentage: 100,
  vacationWeeks: 0,
  benefits: [],
  languages: [],
  company: {
    name: '',
    website: '',
    logoUrl: '',
    about: '',
    address: '',
    companyValues: [],
    numberOfEmployees: 0,
    industry: '',
    contactDetails: '',
  },
};

function emptyProfileCvContent(): ProfileCvContent {
  return {
    profileTitle: '',
    profileWorkExperiences: [],
    profileEducation: [],
    profileProjects: [],
    profileSkills: [],
  };
}

function defaultProfileCvByLocale(): ProfileCvByLocale {
  return { en: emptyProfileCvContent(), fi: emptyProfileCvContent(), sv: emptyProfileCvContent() };
}

/** Defensively coerces a possibly-malformed persisted CV content bucket, same spirit as the old flat-field coercion below. */
function coerceProfileCvContent(value: any): ProfileCvContent {
  return {
    profileTitle: typeof value?.profileTitle === 'string' ? value.profileTitle : '',
    profileWorkExperiences: Array.isArray(value?.profileWorkExperiences) ? value.profileWorkExperiences : [],
    profileEducation: Array.isArray(value?.profileEducation) ? value.profileEducation : [],
    profileProjects: Array.isArray(value?.profileProjects) ? value.profileProjects : [],
    profileSkills: Array.isArray(value?.profileSkills) ? value.profileSkills : [],
  };
}

function emptyApplicationDetails(): ApplicationDetails {
  return { firstName: '', lastName: '', email: '', phone: '', workAuthorization: '', salaryExpectation: 0 };
}

const VALID_WORK_AUTHORIZATION_STATUSES: WorkAuthorizationStatus[] = [
  '',
  'eu_eea_citizen',
  'finnish_citizen',
  'permanent_resident',
  'valid_work_permit',
  'requires_sponsorship',
  'not_authorized',
];

function coerceApplicationDetails(value: any): ApplicationDetails {
  const workAuthorization: WorkAuthorizationStatus = VALID_WORK_AUTHORIZATION_STATUSES.includes(value?.workAuthorization)
    ? value.workAuthorization
    : '';
  return {
    firstName: typeof value?.firstName === 'string' ? value.firstName : '',
    lastName: typeof value?.lastName === 'string' ? value.lastName : '',
    email: typeof value?.email === 'string' ? value.email : '',
    phone: typeof value?.phone === 'string' ? value.phone : '',
    workAuthorization,
    salaryExpectation: typeof value?.salaryExpectation === 'number' ? value.salaryExpectation : 0,
  };
}

function coerceCommuteMode(value: any): CommuteMode | null {
  return typeof value === 'string' && (COMMUTE_MODES as string[]).includes(value) ? (value as CommuteMode) : null;
}

function coerceHomeAddress(value: any): HomeAddress | null {
  if (
    value &&
    typeof value === 'object' &&
    typeof value.label === 'string' &&
    value.point &&
    typeof value.point.lat === 'number' &&
    typeof value.point.lng === 'number'
  ) {
    return { label: value.label, point: { lat: value.point.lat, lng: value.point.lng } };
  }
  return null;
}

/**
 * Builds the per-locale CV container from persisted state, whichever shape it's in:
 * - Already has `profileCvByLocale` (current shape), coerce each bucket defensively.
 * - Old flat shape (`profileTitle`/`profileWorkExperiences`/... at the top level, from before
 *   per-language CVs existed), one-time migration: old single-language content becomes the
 *   `en` bucket, `fi`/`sv` start empty. The user re-fills those themselves (no auto-translation).
 */
function buildProfileCvByLocaleFromPersisted(persisted: any): ProfileCvByLocale {
  if (persisted?.profileCvByLocale && typeof persisted.profileCvByLocale === 'object') {
    return {
      en: coerceProfileCvContent(persisted.profileCvByLocale.en),
      fi: coerceProfileCvContent(persisted.profileCvByLocale.fi),
      sv: coerceProfileCvContent(persisted.profileCvByLocale.sv),
    };
  }
  return {
    en: coerceProfileCvContent(persisted),
    fi: emptyProfileCvContent(),
    sv: emptyProfileCvContent(),
  };
}

const initialState: JobFormState = {
  profilePhotoDataUrl: '',
  cvFileKey: '',
  linkedInUrl: '',
  profileCvByLocale: defaultProfileCvByLocale(),
  homeAddress: null,
  preferredCommuteMode: null,
  applicationDetails: emptyApplicationDetails(),
  currentJob: initialJobDetails,
  desiredJob: {
    titles: [],
    normalisedRoleLevels: [],
    locations: [],
    remoteness: [],
    jobTypes: [],
    salaryMin: 0,
    vacationWeeks: 0,
    benefits: [],
    companyIndustries: [],
  },
};

// Helper function to migrate old state structure
function migrateState(state: any): JobFormState {
  // If state already has new structure, return as is (with CV section defaults if missing)
  if (state.currentJob && typeof state.currentJob === 'object') {
    return {
      profilePhotoDataUrl: state.profilePhotoDataUrl || '',
      cvFileKey: state.cvFileKey || '',
      linkedInUrl: state.linkedInUrl || '',
      profileCvByLocale: buildProfileCvByLocaleFromPersisted(state),
      homeAddress: coerceHomeAddress(state.homeAddress),
      preferredCommuteMode: coerceCommuteMode(state.preferredCommuteMode),
      applicationDetails: coerceApplicationDetails(state.applicationDetails),
      currentJob: { ...initialJobDetails, ...state.currentJob, workPercentage: state.currentJob.workPercentage ?? 100, languages: Array.isArray(state.currentJob.languages) ? state.currentJob.languages : [] },
      desiredJob: state.desiredJob || initialState.desiredJob,
    };
  }

  // Migrate from old structure
  // Handle migration of salary fields: convert salaryRangeMin/Max to salary or salaryMin
  const currentJobSalary = typeof state.salaryRangeMin === 'number' && state.salaryRangeMin > 0
    ? state.salaryRangeMin
    : (typeof state.salary === 'number' ? state.salary : 0);

  const desiredJobSalaryMin = state.desiredJob?.salaryRangeMin
    ? (typeof state.desiredJob.salaryRangeMin === 'number' ? state.desiredJob.salaryRangeMin : 0)
    : (state.desiredJob?.salaryMin
      ? (typeof state.desiredJob.salaryMin === 'number' ? state.desiredJob.salaryMin : 0)
      : 0);

  return {
    profilePhotoDataUrl: state.profilePhotoDataUrl || '',
    // Old cvUrl was a session-only blob URL, never persisted usefully, don't carry it forward.
    cvFileKey: '',
    linkedInUrl: state.linkedInUrl || '',
    // This branch is for the pre-CV-sections shape (no profileTitle/profileWorkExperiences either), so there's nothing to migrate into locale buckets.
    profileCvByLocale: defaultProfileCvByLocale(),
    homeAddress: coerceHomeAddress(state.homeAddress),
    preferredCommuteMode: coerceCommuteMode(state.preferredCommuteMode),
    applicationDetails: coerceApplicationDetails(state.applicationDetails),
    currentJob: {
      title: state.title || '',
      normalisedTitle: state.normalisedTitle || '',
      normalisedRoleLevel: state.normalisedRoleLevel || '',
      description: state.description || '',
      locations: Array.isArray(state.locations) ? state.locations : [],
      remoteness: state.remoteness || '',
      jobType: state.jobType || '',
      salary: currentJobSalary,
      workPercentage: 100,
      vacationWeeks: typeof state.vacationWeeks === 'number' ? state.vacationWeeks : 0,
      benefits: Array.isArray(state.benefits) ? state.benefits : [],
      languages: [],
      company: state.company && typeof state.company === 'object' ? {
        name: state.company.name || '',
        website: state.company.website || '',
        logoUrl: state.company.logoUrl || '',
        about: state.company.about || '',
        address: state.company.address || '',
        companyValues: Array.isArray(state.company.companyValues) ? state.company.companyValues : [],
        numberOfEmployees: typeof state.company.numberOfEmployees === 'number' ? state.company.numberOfEmployees : 0,
        industry: state.company.industry || '',
        contactDetails: state.company.contactDetails || '',
      } : initialJobDetails.company,
    },
    desiredJob: state.desiredJob ? {
      ...state.desiredJob,
      salaryMin: desiredJobSalaryMin,
    } : initialState.desiredJob,
  };
}

const jobFormSlice = createSlice({
  name: 'jobForm',
  initialState,
  reducers: {
    setField: (state, action: PayloadAction<{ field: keyof JobFormState; value: any }>) => {
      const { field, value } = action.payload;
      (state as any)[field] = value;
    },
    /** Sets one CV field (title/experience/education/projects/skills) within a specific language's bucket. */
    setProfileCvField: (
      state,
      action: PayloadAction<{ locale: ProfileLocale; field: keyof ProfileCvContent; value: any }>
    ) => {
      const { locale, field, value } = action.payload;
      (state.profileCvByLocale[locale] as any)[field] = value;
    },
    setCurrentJobField: (state, action: PayloadAction<{ field: keyof JobDetails; value: any }>) => {
      const { field, value } = action.payload;
      (state.currentJob as any)[field] = value;
    },
    setDesiredJobField: (state, action: PayloadAction<{ field: keyof JobFormState['desiredJob']; value: any }>) => {
      const { field, value } = action.payload;
      (state.desiredJob as any)[field] = value;
    },
    setCompanyField: (state, action: PayloadAction<{ field: keyof Company; value: any }>) => {
      const { field, value } = action.payload;
      state.currentJob.company[field] = value;
    },
    /** Sets or clears (pass null) the saved commute-search starting point. */
    setHomeAddress: (state, action: PayloadAction<HomeAddress | null>) => {
      state.homeAddress = action.payload;
    },
    /** Null restores the default (public transport). */
    setPreferredCommuteMode: (state, action: PayloadAction<CommuteMode | null>) => {
      state.preferredCommuteMode = action.payload;
    },
    setApplicationDetailsField: (
      state,
      action: PayloadAction<{ field: keyof ApplicationDetails; value: any }>
    ) => {
      const { field, value } = action.payload;
      (state.applicationDetails as any)[field] = value;
    },
    addBenefit: (state, action: PayloadAction<string>) => {
      state.currentJob.benefits.push(action.payload);
    },
    removeBenefit: (state, action: PayloadAction<number>) => {
      state.currentJob.benefits.splice(action.payload, 1);
    },
    addDesiredBenefit: (state, action: PayloadAction<string>) => {
      if (!state.desiredJob.benefits.includes(action.payload)) {
        state.desiredJob.benefits.push(action.payload);
      }
    },
    removeDesiredBenefit: (state, action: PayloadAction<string>) => {
      state.desiredJob.benefits = state.desiredJob.benefits.filter((b) => b !== action.payload);
    },
    resetForm: () => initialState,
    /** Adopts the server's copy of the profile form (e.g. after a user-state sync conflict) so local state converges instead of re-fighting the server forever. Only overwrites fields the server actually sent. */
    applyServerJobForm: (state, action: PayloadAction<Partial<JobFormState>>) => {
      const server = action.payload;
      if (typeof server.profilePhotoDataUrl === 'string') state.profilePhotoDataUrl = server.profilePhotoDataUrl;
      if (typeof server.cvFileKey === 'string') state.cvFileKey = server.cvFileKey;
      if (typeof server.linkedInUrl === 'string') state.linkedInUrl = server.linkedInUrl;
      if (server.profileCvByLocale && typeof server.profileCvByLocale === 'object') {
        state.profileCvByLocale = server.profileCvByLocale;
      }
      if (server.currentJob && typeof server.currentJob === 'object') {
        state.currentJob = server.currentJob;
      }
      if (server.desiredJob && typeof server.desiredJob === 'object') {
        state.desiredJob = server.desiredJob;
      }
      if (server.homeAddress !== undefined) {
        state.homeAddress = coerceHomeAddress(server.homeAddress);
      }
      if (server.preferredCommuteMode !== undefined) {
        state.preferredCommuteMode = coerceCommuteMode(server.preferredCommuteMode);
      }
      if (server.applicationDetails && typeof server.applicationDetails === 'object') {
        state.applicationDetails = coerceApplicationDetails(server.applicationDetails);
      }
    },
  },
  extraReducers: (builder) => {
    // Handle REHYDRATE to migrate old state structure
    builder.addCase(REHYDRATE, (state, action: any) => {
      const persistedState = action.payload?.jobForm;
      if (persistedState) {
        // If state doesn't have currentJob, migrate it
        if (!persistedState.currentJob || typeof persistedState.currentJob !== 'object') {
          return migrateState(persistedState);
        }
        // Migrate salary fields if they still use old structure
        const migratedCurrentJob = persistedState.currentJob ? {
          ...initialJobDetails,
          ...persistedState.currentJob,
          salary: persistedState.currentJob.salary !== undefined
            ? persistedState.currentJob.salary
            : (persistedState.currentJob.salaryRangeMin !== undefined
              ? persistedState.currentJob.salaryRangeMin
              : 0),
          workPercentage: persistedState.currentJob.workPercentage ?? 100,
        } : initialJobDetails;

        const migratedDesiredJob = persistedState.desiredJob ? {
          ...persistedState.desiredJob,
          salaryMin: persistedState.desiredJob.salaryMin !== undefined
            ? persistedState.desiredJob.salaryMin
            : (persistedState.desiredJob.salaryRangeMin !== undefined
              ? persistedState.desiredJob.salaryRangeMin
              : 0),
        } : initialState.desiredJob;

        // Ensure all required fields exist (including CV sections)
        return {
          ...initialState,
          ...persistedState,
          currentJob: migratedCurrentJob,
          desiredJob: migratedDesiredJob,
          profilePhotoDataUrl: persistedState.profilePhotoDataUrl || '',
          cvFileKey: persistedState.cvFileKey || '',
          linkedInUrl: persistedState.linkedInUrl || '',
          profileCvByLocale: buildProfileCvByLocaleFromPersisted(persistedState),
          homeAddress: coerceHomeAddress(persistedState.homeAddress),
          preferredCommuteMode: coerceCommuteMode(persistedState.preferredCommuteMode),
          applicationDetails: coerceApplicationDetails(persistedState.applicationDetails),
        };
      }
      return state;
    });
  },
});

export const {
  setField,
  setProfileCvField,
  setCurrentJobField,
  setDesiredJobField,
  setCompanyField,
  setHomeAddress,
  setPreferredCommuteMode,
  setApplicationDetailsField,
  addBenefit,
  removeBenefit,
  addDesiredBenefit,
  removeDesiredBenefit,
  resetForm,
  applyServerJobForm,
} = jobFormSlice.actions;
export default jobFormSlice.reducer;
